<template>
  <BaseDialog
    title="編輯預約備註"
    :close-on-click-modal="false"
    width="610px"
    :btn1-loading="loading"
    @close="$emit('close')"
    @cancel="$emit('close')"
    @confirm="onConfirm"
  >
    <div style="margin-bottom: 40px">
      <BobForm
        :formRef.sync="formRef"
        :model="formData"
        :fields="displayFields"
        :rules="formRules"
        label-position="top"
      />
    </div>
  </BaseDialog>
</template>

<script>
import { UpdateReservationParameter } from '@/api/reservation'
import BobForm from '@/components/BobForm.vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import notifyMessage from '@/config/notifyMessage'
import { useShop } from '@/use/shop'
import { useBaseForm } from '@/use/useForm'
import { noEmptyRules, urlRules } from '@/validation'
import { get } from 'lodash'
import { computed, defineComponent, onBeforeMount, provide } from 'vue'

export default defineComponent({
  name: 'EditAppointmentNoteSettingsModal',
  components: {
    BaseDialog,
    BobForm,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formRef, formData, initFormData, checkForm, loading } = useBaseForm()

    initFormData({
      enable: false,
      content: undefined,
      btn1: {
        enable: false,
        label: undefined,
        link: undefined,
      },
      btn2: {
        enable: false,
        label: undefined,
        link: undefined,
      },
    })

    const displayFields = [
      {
        prop: 'enable',
        label: '顯示預約備註',
        value: formData.enable,
        body: {
          component: 'BaseElSwitch',
          props: {
            activeText: '開啟',
            inactiveText: '關閉',
          },
        },
      },
      {
        prop: 'content',
        label: '備註內容',
        body: {
          component: () => import('@/components/BaseTextEditor.vue'),
          props: {
            value: formData.content,
          },
          on: {
            'update:value': (val) => {
              formData.content = val
            },
          },
        },
      },
      {
        prop: 'btn1',
        label: '按鈕1',
        body: {
          component: () => import('./EditAppointmentNoteBtnSetting.vue'),
          props: {
            propKey: 'btn1',
            value: formData.btn1,
          },
        },
      },
      {
        prop: 'btn2',
        label: '按鈕2',
        body: {
          component: () => import('./EditAppointmentNoteBtnSetting.vue'),
          props: {
            propKey: 'btn2',
            value: formData.btn2,
          },
        },
      },
    ]

    const formRules = {
      'btn1.label': [noEmptyRules()],
      'btn1.link': [noEmptyRules(), urlRules()],
      'btn2.label': [noEmptyRules()],
      'btn2.link': [noEmptyRules(), urlRules()],
    }

    const submitPayload = computed(() => {
      const payload = {
        shopId: shopId.value,
        enableTimeSlotMemo: formData.enable,
        timeSlotMemoSetting: {
          content: formData.content,
          buttons: [],
        },
      }

      if (formData.btn1.enable) {
        payload.timeSlotMemoSetting.buttons.push({
          index: 0,
          type: 'link',
          text: formData.btn1.label,
          url: formData.btn1.link,
        })
      }

      if (formData.btn2.enable) {
        payload.timeSlotMemoSetting.buttons.push({
          index: 1,
          type: 'link',
          text: formData.btn2.label,
          url: formData.btn2.link,
        })
      }

      return payload
    })

    const onConfirm = async () => {
      if (loading.value) return
      const isValid = await checkForm(formRef.value)
      if (!isValid) {
        loading.value = false
        return
      }

      loading.value = true

      const [, errMsg] = await UpdateReservationParameter(submitPayload.value)
      loading.value = false

      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      window.$message.success(notifyMessage.updateSuccess)

      emit('refresh')
      emit('close')
    }

    const syncFormData = () => {
      formData.enable = get(props.configData, 'enableTimeSlotMemo', false)
      formData.content = get(props.configData, 'timeSlotMemoSetting.content')
      for (const btnSetting of get(props.configData, 'timeSlotMemoSetting.buttons')) {
        if (btnSetting.index === 0) {
          formData.btn1.enable = true
          formData.btn1.label = btnSetting.text
          formData.btn1.link = btnSetting.url
        } else if (btnSetting.index === 1) {
          formData.btn2.enable = true
          formData.btn2.label = btnSetting.text
          formData.btn2.link = btnSetting.url
        }
      }
    }

    provide('formData', formData)

    onBeforeMount(() => {
      syncFormData()
    })

    return {
      formRef,
      formData,
      formRules,
      displayFields,
      initFormData,
      checkForm,
      loading,
      onConfirm,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
